import { isValidSearchText, searchTextToSend } from '@/lib/search-text';
import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/**
 * 슬롯(Unit) 판매 현황 한 건 — 관리 room-sales 목록의 sale_scope=UNIT 정확 선택(최대 1건)이다.
 * 로컬 BE candidate(010, 미배포)의 UnitSaleAdminEntryResponse(extra=forbid)를 따른다.
 */
export interface UnitSaleEntry {
  room_id: string;
  unit_id: string;
  unit_version: number;
  product: string | null;
  channel: string | null;
  first_move_in_date: string | null;
  daily_price_minor: number | null;
  deposit_amount_minor: number | null;
  cleaning_amount_minor: number | null;
  allowed_actions: string[];
  status: string;
  reasons: string[];
  sale_revision: number | null;
  pending_outcome: string | null;
  residual_booking_count: number;
}

type Obj = Record<string, unknown>;
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '슬롯 판매 현황을 확인하지 못했습니다.',
  });
const obj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);
const integer = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0;
const nullableInteger = (v: unknown) => v === null || integer(v);
const nullableText = (v: unknown) => v === null || typeof v === 'string';
const strings = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((item) => typeof item === 'string');
const isDate = (v: unknown) =>
  v === null ||
  (typeof v === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(v) &&
    new Date(`${v}T00:00:00Z`).toISOString().slice(0, 10) === v);
const same = (a: unknown, b: string) =>
  typeof a === 'string' && isUuid(a) && a.toLowerCase() === b.toLowerCase();
const entryKeys = [
  'room_id',
  'room_type_id',
  'room_name',
  'room_type_name',
  'product',
  'channel',
  'first_move_in_date',
  'longterm_contract_end_date',
  'daily_price_minor',
  'price_currency',
  'price_source',
  'price_revision',
  'allowed_actions',
  'residual_booking_count',
  'residual_booking_last_date',
  'status',
  'reasons',
  'last_success_at',
  'sale_revision',
  'pending_outcome',
  'pending_observed_at',
  'sale_scope',
  'unit_id',
  'unit_name',
  'unit_version',
  'classification',
  'deposit_amount_minor',
  'cleaning_amount_minor',
  'price_cas',
];

/** 관리 목록 한 행 — 정확 선택 행에 객실·슬롯 이름과 층, 판매 분류를 더한다. */
export interface UnitSaleListItem extends UnitSaleEntry {
  room_name: string;
  room_type_name: string;
  unit_name: string | null;
  floor: number | null;
  classification: string | null;
}

function decodeItem(item: unknown): UnitSaleListItem {
  if (
    !obj(item) ||
    // floor는 선택 key다(미설정이면 NULL 또는 생략).
    Object.keys(item).some((key) => key !== 'floor' && !entryKeys.includes(key)) ||
    !entryKeys.every((key) => Object.hasOwn(item, key)) ||
    item.sale_scope !== 'UNIT' ||
    typeof item.unit_id !== 'string' ||
    !isUuid(item.unit_id) ||
    typeof item.room_id !== 'string' ||
    !isUuid(item.room_id) ||
    typeof item.room_name !== 'string' ||
    typeof item.room_type_name !== 'string' ||
    !nullableText(item.unit_name) ||
    !nullableText(item.classification) ||
    !(item.floor === undefined || item.floor === null || Number.isSafeInteger(item.floor)) ||
    !integer(item.unit_version) ||
    !nullableText(item.product) ||
    !nullableText(item.channel) ||
    !isDate(item.first_move_in_date) ||
    !nullableInteger(item.daily_price_minor) ||
    !nullableInteger(item.deposit_amount_minor) ||
    !nullableInteger(item.cleaning_amount_minor) ||
    !strings(item.allowed_actions) ||
    !strings(item.reasons) ||
    typeof item.status !== 'string' ||
    !nullableInteger(item.sale_revision) ||
    !nullableText(item.pending_outcome) ||
    !integer(item.residual_booking_count)
  )
    throw invalid();
  return {
    room_id: item.room_id,
    unit_id: item.unit_id,
    unit_version: item.unit_version,
    room_name: item.room_name,
    room_type_name: item.room_type_name,
    unit_name: item.unit_name,
    floor: (item.floor as number | null | undefined) ?? null,
    classification: item.classification,
    product: item.product,
    channel: item.channel,
    first_move_in_date: item.first_move_in_date as string | null,
    daily_price_minor: item.daily_price_minor,
    deposit_amount_minor: item.deposit_amount_minor,
    cleaning_amount_minor: item.cleaning_amount_minor,
    allowed_actions: item.allowed_actions,
    status: item.status,
    reasons: item.reasons,
    sale_revision: item.sale_revision,
    pending_outcome: item.pending_outcome,
    residual_booking_count: item.residual_booking_count,
  };
}

export async function readUnitSaleEntry(
  client: ServiceClient,
  propertyId: string,
  roomId: string,
  unitId: string,
  signal?: AbortSignal,
): Promise<UnitSaleEntry> {
  if (!isUuid(propertyId) || !isUuid(roomId) || !isUuid(unitId)) throw invalid();
  const raw = await client.get<unknown>(`admin/properties/${propertyId}/room-sales`, {
    params: { sale_scope: 'UNIT', room_id: roomId, unit_id: unitId, limit: 1 },
    headers: { 'Cache-Control': 'no-store' },
    signal,
  });
  if (
    !obj(raw) ||
    !same(raw.property_id, propertyId) ||
    typeof raw.property_name !== 'string' ||
    !Array.isArray(raw.items) ||
    raw.items.length !== 1 ||
    raw.next_cursor !== null
  )
    throw invalid();
  const item = decodeItem(raw.items[0]);
  if (!same(item.unit_id, unitId) || !same(item.room_id, roomId)) throw invalid();
  return item;
}

export interface UnitSalesPageOptions {
  limit: number;
  cursor: string | null;
  /** 서버가 받는 정렬 값만: floor(기본)·name. */
  sortBy: 'floor' | 'name';
  sortDirection: 'asc' | 'desc';
  /** signed int32 정수. 비우면 보내지 않는다(null을 문자열로 보내지 않는다). */
  floor: number | null;
  /** 층 미지정(NULL) Room의 Unit만. floor와 함께 쓰지 않는다. */
  floorIsNull?: boolean;
  /** Room 또는 Unit 이름 부분 일치. trim 뒤 1~100자만 보낸다. */
  q?: string | null;
}
export interface UnitSalesPage {
  property_id: string;
  property_name: string;
  items: UnitSaleListItem[];
  next_cursor: string | null;
}
const cursorValid = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.length >= 1 &&
  value.length <= 2048 &&
  /^[A-Za-z0-9_-]+$/.test(value);

/** 관리 room-sales 목록의 sale_scope=UNIT 한 쪽. 전체 개수·검색은 서버 계약에 없어 요청하지 않는다. */
export async function readUnitSalesPage(
  client: ServiceClient,
  propertyId: string,
  options: UnitSalesPageOptions,
  signal?: AbortSignal,
): Promise<UnitSalesPage> {
  const q = searchTextToSend(options.q);
  if (
    (q !== null && !isValidSearchText(q)) ||
    (options.floor !== null && options.floorIsNull === true) ||
    !isUuid(propertyId) ||
    !Number.isInteger(options.limit) ||
    options.limit < 1 ||
    options.limit > 50 ||
    (options.cursor !== null && !cursorValid(options.cursor)) ||
    !['floor', 'name'].includes(options.sortBy) ||
    !['asc', 'desc'].includes(options.sortDirection) ||
    (options.floor !== null &&
      (!Number.isInteger(options.floor) || options.floor < -(2 ** 31) || options.floor >= 2 ** 31))
  )
    throw invalid();
  const raw = await client.get<unknown>(`admin/properties/${propertyId}/room-sales`, {
    params: {
      sale_scope: 'UNIT',
      limit: options.limit,
      sort_by: options.sortBy,
      sort_direction: options.sortDirection,
      ...(options.cursor ? { cursor: options.cursor } : {}),
      ...(options.floor === null ? {} : { floor: options.floor }),
      ...(options.floorIsNull === true ? { floor_is_null: 'true' } : {}),
      ...(q === null ? {} : { q }),
    },
    headers: { 'Cache-Control': 'no-store' },
    signal,
  });
  if (
    !obj(raw) ||
    Object.keys(raw).length !== 4 ||
    !same(raw.property_id, propertyId) ||
    typeof raw.property_name !== 'string' ||
    !Array.isArray(raw.items) ||
    raw.items.length > options.limit ||
    !(raw.next_cursor === null || cursorValid(raw.next_cursor))
  )
    throw invalid();
  const items = raw.items.map(decodeItem);
  if (new Set(items.map((item) => item.unit_id.toLowerCase())).size !== items.length)
    throw invalid();
  return {
    property_id: raw.property_id as string,
    property_name: raw.property_name,
    items,
    next_cursor: raw.next_cursor,
  };
}
